import React, { useState, useEffect, useCallback } from 'react';
import contentService from '../services/contentService';
import './PostsPublicados.css';

import Loading from '../components/Loading';
/* ─── helpers ─────────────────────────────────── */
const fmtN = (n) => {
  if (n == null || n === 0) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString('es-CL');
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
};

const MONTH_NAMES = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre',
];

/* ─── tipo badge ──────────────────────────────── */
const TYPE_STYLE = {
  VIDEO:          { label: 'REEL',      bg: '#fce4ec', color: '#c2185b' },
  IMAGE:          { label: 'FOTO',      bg: '#e3f2fd', color: '#1565c0' },
  CAROUSEL_ALBUM: { label: 'CARRUSEL',  bg: '#ede7f6', color: '#4527a0' },
};

function TypeBadge({ mediaType, productType }) {
  let key = mediaType;
  // Si es video con product_type reel/clips → badge Reel
  if (mediaType === 'VIDEO' && (productType === 'reel' || productType === 'clips')) {
    key = 'VIDEO';
  }
  const s = TYPE_STYLE[key] || { label: mediaType || '—', bg: '#f1f5f9', color: '#475569' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 20,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.04em',
      background: s.bg,
      color: s.color,
      whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
}

/* ─── thumbnail ───────────────────────────────── */
function Thumb({ url, mediaType }) {
  const [err, setErr] = useState(false);
  const icon = mediaType === 'VIDEO' ? '▶' : mediaType === 'CAROUSEL_ALBUM' ? '⊞' : '□';
  if (url && !err) {
    return (
      <img
        src={url} alt=""
        onError={() => setErr(true)}
        className="pub-thumb"
      />
    );
  }
  return (
    <div className="pub-thumb pub-thumb-placeholder">
      {icon}
    </div>
  );
}

/* ─── PostRow ─────────────────────────────────── */
function PostRow({ p, followers }) {
  const [expanded, setExpanded] = useState(false);
  const caption = p.caption || '';

  return (
    <>
      <tr
        className="pub-row"
        onClick={() => setExpanded(e => !e)}
        style={{ cursor: 'pointer' }}
      >
        {/* Thumbnail + caption */}
        <td className="pub-td pub-td-post">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Thumb url={p.thumbnail_url} mediaType={p.media_type} />
            <div style={{ minWidth: 0 }}>
              <TypeBadge mediaType={p.media_type} productType={p.product_type} />
              <div style={{
                fontSize: 12, color: '#475569', marginTop: 3,
                overflow: 'hidden', textOverflow: 'ellipsis',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                maxWidth: 300,
              }}>
                {caption || <span style={{ color: '#cbd5e1', fontStyle: 'italic' }}>Sin caption</span>}
              </div>
              {p.is_collab && p.collab_usernames?.length > 0 && (
                <div style={{ marginTop: 3, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {p.collab_usernames.map(u => (
                    <span key={u} style={{
                      fontSize: 10, background: '#e0f2fe', color: '#0369a1',
                      borderRadius: 4, padding: '1px 5px', fontWeight: 600,
                    }}>
                      @{u}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </td>

        {/* Fecha */}
        <td className="pub-td pub-td-date">
          <span style={{ fontSize: 12, color: '#64748b' }}>{fmtDate(p.posted_at)}</span>
        </td>

        {/* Likes */}
        <td className="pub-td pub-td-num">
          <span style={{ fontWeight: 600, color: '#1e293b' }}>{fmtN(p.like_count)}</span>
        </td>

        {/* Comentarios */}
        <td className="pub-td pub-td-num">
          <span style={{ color: '#475569' }}>{fmtN(p.comments_count)}</span>
        </td>

        {/* Views */}
        <td className="pub-td pub-td-num">
          {p.views_count
            ? <span style={{ color: '#475569' }}>{fmtN(p.views_count)}</span>
            : <span style={{ color: '#cbd5e1' }}>—</span>
          }
        </td>

        {/* ER */}
        <td className="pub-td pub-td-num">
          {p.engagement_rate != null
            ? <span style={{ fontWeight: 600, color: '#6366f1' }}>{p.engagement_rate.toFixed(2)}%</span>
            : <span style={{ color: '#cbd5e1' }}>—</span>
          }
        </td>

        {/* VR — solo Reels */}
        <td className="pub-td pub-td-num">
          {p.view_rate != null
            ? <span style={{ fontWeight: 600, color: '#0891b2' }}>{p.view_rate.toFixed(2)}%</span>
            : <span style={{ color: '#cbd5e1' }}>—</span>
          }
        </td>

        {/* Ver */}
        <td className="pub-td" style={{ textAlign: 'center' }}>
          <a
            href={p.permalink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            style={{ fontSize: 12, color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}
          >
            Ver →
          </a>
        </td>
      </tr>

      {/* Caption expandido */}
      {expanded && caption && (
        <tr>
          <td colSpan={8} style={{ padding: '0 1rem 0.75rem 4rem', background: '#fafbff' }}>
            <div style={{
              fontSize: 12, color: '#475569', lineHeight: 1.6,
              background: '#f8fafc', borderRadius: 8, padding: '0.6rem 0.9rem',
              whiteSpace: 'pre-wrap',
            }}>
              {caption}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* ─── Component principal ─────────────────────── */
export default function PostsPublicados() {
  const now = new Date();
  const [year,  setYear]  = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [showAll, setShowAll] = useState(false);

  const [marca, setMarca]               = useState(null);
  const [competenciaProfiles, setCompetenciaProfiles] = useState([]);
  const [referenciaProfiles, setReferenciaProfiles]   = useState([]);
  const [activeProfileId, setActiveProfileId] = useState(null); // null = Mi Marca
  const [activeProfile, setActiveProfile]     = useState(null);
  const [posts, setPosts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const [sortField, setSortField] = useState('-posted_at');
  const [filterType, setFilterType] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { ordering: sortField };
      if (!showAll) {
        params.year  = year;
        params.month = month;
      }
      if (activeProfileId) {
        params.profile_id = activeProfileId;
      }
      const res = await contentService.getMarcaPosts(params);
      setMarca(res.data.marca || null);
      setActiveProfile(res.data.active_profile || res.data.marca || null);
      setCompetenciaProfiles(res.data.competencia_profiles || []);
      setReferenciaProfiles(res.data.referencia_profiles || []);
      setPosts(res.data.posts || []);
    } catch (e) {
      setError('Error al cargar los posts.');
    } finally {
      setLoading(false);
    }
  }, [year, month, showAll, sortField, activeProfileId]);

  useEffect(() => { load(); }, [load]);

  /* ── filtro local por tipo ── */
  const filtered = filterType
    ? posts.filter(p => p.media_type === filterType)
    : posts;

  /* ── stats ── */
  const totLikes    = posts.reduce((s, p) => s + (p.like_count    || 0), 0);
  const totComments = posts.reduce((s, p) => s + (p.comments_count || 0), 0);
  const totViews    = posts.reduce((s, p) => s + (p.views_count   || 0), 0);
  const withEr      = posts.filter(p => p.engagement_rate != null);
  const avgEr       = withEr.length
    ? (withEr.reduce((s, p) => s + p.engagement_rate, 0) / withEr.length).toFixed(2)
    : null;
  const reels       = posts.filter(p => p.media_type === 'VIDEO').length;
  const fotos       = posts.filter(p => p.media_type === 'IMAGE').length;
  const carruseles  = posts.filter(p => p.media_type === 'CAROUSEL_ALBUM').length;
  const withVr      = posts.filter(p => p.view_rate != null);
  const avgVr       = withVr.length
    ? (withVr.reduce((s, p) => s + p.view_rate, 0) / withVr.length).toFixed(2)
    : null;

  /* ── sort toggle ── */
  const toggleSort = (field) => {
    setSortField(prev => prev === `-${field}` ? field : `-${field}`);
  };

  const SortIcon = ({ field }) => {
    if (sortField === `-${field}`) return <span style={{ marginLeft: 3, fontSize: 9 }}>▼</span>;
    if (sortField === field)       return <span style={{ marginLeft: 3, fontSize: 9 }}>▲</span>;
    return <span style={{ marginLeft: 3, fontSize: 9, opacity: 0.3 }}>⇅</span>;
  };

  /* ── sin marca configurada ── */
  if (!loading && !marca) {
    return (
      <div className="pub-page">
        <div className="pub-header">
          <div className="pub-header-left">
            <h1 className="pig-topbar-title">
              <i className="bi bi-bar-chart-line" /> Publicados
            </h1>
            <p className="pig-topbar-sub">Posts de Mi Marca</p>
          </div>
        </div>
        <div className="pub-no-marca">
          <i className="bi bi-instagram" style={{ fontSize: 40, color: '#cbd5e1' }} />
          <p style={{ fontWeight: 600, color: '#475569', marginTop: 12 }}>
            No hay una marca configurada
          </p>
          <p style={{ fontSize: 13, color: '#94a3b8' }}>
            Ve a <a href="/mercado" style={{ color: '#6366f1' }}>Mercado</a> y selecciona Mi Marca para ver sus posts.
          </p>
        </div>
      </div>
    );
  }

  /* ── perfiles disponibles en el selector ── */
  const hasOtherProfiles = competenciaProfiles.length > 0 || referenciaProfiles.length > 0;

  return (
    <div className="pub-page">

      {/* ── Header ── */}
      <div className="pub-header">
        <div className="pub-header-left">
          <h1 className="pig-topbar-title">
            <i className="bi bi-bar-chart-line" /> Publicados
          </h1>
          {activeProfile && (
            <p className="pig-topbar-sub" style={{ margin: 0 }}>
              @{activeProfile.username}
              {activeProfile.followers_count ? ` · ${fmtN(activeProfile.followers_count)} seguidores` : ''}
            </p>
          )}
        </div>

        <div className="pub-header-right">
          {/* Selector de perfil (Marca + Competencia + Referencia) */}
          {(marca || hasOtherProfiles) && (
            <select
              className="pub-filter-select"
              value={activeProfileId ?? ''}
              onChange={e => {
                const val = e.target.value;
                setActiveProfileId(val === '' ? null : Number(val));
                setFilterType('');
              }}
            >
              {marca && (
                <optgroup label="Mi Marca">
                  <option value="">@{marca.username}</option>
                </optgroup>
              )}
              {competenciaProfiles.length > 0 && (
                <optgroup label="Competencia">
                  {competenciaProfiles.map(p => (
                    <option key={p.id} value={p.id}>@{p.username}</option>
                  ))}
                </optgroup>
              )}
              {referenciaProfiles.length > 0 && (
                <optgroup label="Referencia">
                  {referenciaProfiles.map(p => (
                    <option key={p.id} value={p.id}>@{p.username}</option>
                  ))}
                </optgroup>
              )}
            </select>
          )}

          {/* Toggle: todo / por mes */}
          <button
            className={`pub-filter-select pub-toggle-btn${showAll ? ' active' : ''}`}
            onClick={() => setShowAll(v => !v)}
            title="Mostrar todos los posts sin filtro de mes"
          >
            {showAll ? 'Todos los posts' : 'Por mes'}
          </button>

          {!showAll && (<>
            <select className="pub-filter-select" value={month} onChange={e => setMonth(Number(e.target.value))}>
              {MONTH_NAMES.map((n, i) => <option key={i} value={i + 1}>{n}</option>)}
            </select>
            <select className="pub-filter-select" value={year} onChange={e => setYear(Number(e.target.value))}>
              {[now.getFullYear() - 1, now.getFullYear()].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </>)}
        </div>
      </div>

      {/* ── Stats bar ── */}
      <div className="pub-stats-bar">
        <div className="pub-stat">
          <span className="pub-stat-val">{posts.length}</span>
          <span className="pub-stat-lbl">Posts</span>
        </div>
        <div className="pub-stat">
          <span className="pub-stat-val">{fmtN(totLikes)}</span>
          <span className="pub-stat-lbl">Likes</span>
        </div>
        <div className="pub-stat">
          <span className="pub-stat-val">{fmtN(totComments)}</span>
          <span className="pub-stat-lbl">Comentarios</span>
        </div>
        <div className="pub-stat">
          <span className="pub-stat-val">{fmtN(totViews)}</span>
          <span className="pub-stat-lbl">Vistas</span>
        </div>
        <div className="pub-stat">
          <span className="pub-stat-val">{avgEr != null ? `${avgEr}%` : '—'}</span>
          <span className="pub-stat-lbl">ER prom.</span>
        </div>
        <div className="pub-stat">
          <span className="pub-stat-val" style={{ color: '#0891b2' }}>{avgVr != null ? `${avgVr}%` : '—'}</span>
          <span className="pub-stat-lbl">VR prom. reels</span>
        </div>
        <div className="pub-stat-sep" />
        <div className="pub-stat">
          <span className="pub-stat-val" style={{ color: '#c2185b' }}>{reels}</span>
          <span className="pub-stat-lbl">Reels</span>
        </div>
        <div className="pub-stat">
          <span className="pub-stat-val" style={{ color: '#1565c0' }}>{fotos}</span>
          <span className="pub-stat-lbl">Fotos</span>
        </div>
        <div className="pub-stat">
          <span className="pub-stat-val" style={{ color: '#4527a0' }}>{carruseles}</span>
          <span className="pub-stat-lbl">Carruseles</span>
        </div>
      </div>

      {/* ── Filtros tipo ── */}
      <div className="pub-type-filters">
        {[
          ['', 'Todos'],
          ['VIDEO', 'Reels / Video'],
          ['IMAGE', 'Fotos'],
          ['CAROUSEL_ALBUM', 'Carruseles'],
        ].map(([val, label]) => (
          <button
            key={val}
            className={`pub-type-btn${filterType === val ? ' active' : ''}`}
            onClick={() => setFilterType(val)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Table ── */}
      {loading && <Loading fullScreen={false} mensaje="Cargando posts" />}
      {!loading && error && <div className="pub-empty"><p style={{ color: '#dc2626' }}>{error}</p></div>}
      {!loading && !error && filtered.length === 0 && (
        <div className="pub-empty">
          <p>No hay posts {!showAll ? `en ${MONTH_NAMES[month - 1]} ${year}` : ''} para @{activeProfile?.username}.</p>
          {!showAll && (
            <button className="pub-filter-select" style={{ marginTop: 8 }} onClick={() => setShowAll(true)}>
              Ver todos los posts
            </button>
          )}
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="pub-table-wrap">
          <table className="pub-table">
            <thead>
              <tr>
                <th className="pub-th" style={{ minWidth: 300 }}>Post</th>
                <th
                  className="pub-th pub-th-sort"
                  style={{ minWidth: 120 }}
                  onClick={() => toggleSort('posted_at')}
                >
                  Fecha <SortIcon field="posted_at" />
                </th>
                <th
                  className="pub-th pub-th-sort pub-th-num"
                  onClick={() => toggleSort('like_count')}
                >
                  Likes <SortIcon field="like_count" />
                </th>
                <th className="pub-th pub-th-num">Comentarios</th>
                <th
                  className="pub-th pub-th-sort pub-th-num"
                  onClick={() => toggleSort('views_count')}
                >
                  Vistas <SortIcon field="views_count" />
                </th>
                <th className="pub-th pub-th-num">ER</th>
                <th className="pub-th pub-th-num" title="View Rate: vistas / seguidores (solo Reels)">VR</th>
                <th className="pub-th" style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <PostRow key={p.id} p={p} followers={activeProfile?.followers_count} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
